import {getConsent, storedPurposesVersion} from "consent/consentStore";
import type {ConsentState} from "consent/consentStore";
import {clearSignUpPending} from "./signUpMark";
import {setPanelLatencyReporter} from "perf/panelLatency";
import {googleTagConfig, metaPixelId, redditPixelId} from "./analyticsConfig";
import {googleTaggingAllowed} from "./googleConsentMode";
import {
	clearGoogleAdvertisingCookies, clearGoogleAnalyticsCookies, startGoogleTagging, stopGoogleTagging,
	updateGoogleConsent,
} from "./googleTags";
import {clearAnalyticsClientId} from "./analyticsClientId";
import {forgetLastReportedPageView} from "./pageView";
import {sendAnalyticsEvent} from "./analyticsEventClient";
import type {AnalyticsEventName, AnalyticsEventParams} from "./analyticsEvents";
import {identifyRedditVisitor, redditPixelRunning, startRedditPixel, stopRedditPixel, trackRedditPageVisit,
	trackRedditSignUp} from "./redditPixel";
import type {RedditMatchKeys} from "./redditPixel";
import {identifyMetaVisitor, metaPixelRunning, startMetaPixel, stopMetaPixel, trackMetaPageView,
	trackMetaSignUp} from "./metaPixel";

/**
 * The consent → analytics orchestrator (ui#72, rebuilt for Consent Mode v2 in ui#256).
 *
 * {@link syncAnalyticsWithConsent} is the ONLY entry point, and it is idempotent: call it on mount, on every
 * consent change, and as often as you like. It answers the current consent state each time —
 *
 *   - no purpose granted → nothing loads, and anything already running is stopped and its cookies cleared;
 *   - a purpose granted → the Google Ads tag starts (see {@link startGoogleTagging} for the Basic Consent
 *     Mode ordering), or, if already running, is told about the change;
 *   - ANALYTICS granted → performance telemetry (Core Web Vitals, panel-open latency) is armed;
 *   - ANALYTICS denied or withdrawn → the first-party analytics client id is deleted.
 *
 * Telemetry rides the analytics purpose specifically, not "any tag is running": a visitor who granted only
 * advertising measurement did not agree to have their device's performance measured.
 *
 * GA4 is not in this browser at all since kitecorp/transferify-api-service#1929. Measurement goes to our
 * own api, which forwards it to GA4 server-side; the Google tag that remains carries the Ads id alone and
 * sends no event, so it is a remarketing page hit and nothing more.
 */

/**
 * Single-flight latch. Two callers in the same tick (the banner mounting while the accept click lands) must
 * await ONE bootstrap: the previous version set its guard after an await, so both passed it and the second
 * pushed a `consent default` AFTER the first had applied the visitor's grant — silently resetting it.
 */
let telemetryWiring: Promise<void> | null = null;

/** Whether the tag has ever been started in this session (drives update-vs-start). */
let tagRunning = false;

/**
 * The match keys the running pixel was last told about, so a re-sync that carries the SAME ones sends
 * nothing. {@link identifyRedditVisitor} fires a real `PageVisit`, and this function runs on every
 * consent change — without this, a signed-in visitor opening cookie settings and pressing Save added a
 * page view to the Reddit account each time.
 */
let redditIdentity: string | null = null;

/**
 * Forwards a telemetry event to the first-party analytics ingest.
 *
 * Analytics consent is still re-checked at SEND time rather than at registration — web-vitals observers
 * cannot be unwired, so a withdrawal has to be honoured at the point of send — but the check now lives
 * inside {@link sendAnalyticsEvent}, which every caller shares. One gate, not one per sender.
 */
function reportTelemetry(name: AnalyticsEventName, params: AnalyticsEventParams): void {
	sendAnalyticsEvent(name, params);
}

/** Arms the performance reporters exactly once per session. */
function wireTelemetry(): Promise<void> {
	if (telemetryWiring === null) {
		telemetryWiring = (async () => {
			setPanelLatencyReporter(reportTelemetry);
			try {
				// Lazy: `web-vitals`, the metric mappers and the first-party beacon all stay in this chunk,
				// loaded only after a consenting visitor arrives. It must never become a static import —
				// this module is eager (RootLayout mounts useAnalyticsConsent), so a static edge here would
				// put the reporter into the landing page's pre-hydration graph and inflate the very LCP it
				// reports. `scripts/vitals-lazy-sweep.mjs` reads the built tree and fails if it does.
				const {armVitals} = await import("vitals/armVitals");
				armVitals(reportTelemetry, {
					hostname: window.location.hostname,
					// Read at CALL time, not captured: the route class is stamped onto each reading as it
					// arrives, and on a single-page app the visitor may have navigated since arming.
					pathname: () => window.location.pathname,
					// Two targets, because the two events are fired at different ones: `pagehide` at the
					// window, `visibilitychange` at the document. A single target loses one of them.
					windowTarget: window,
					documentTarget: document,
					// The same send-time gate reportTelemetry applies, for the same reason: web-vitals
					// observers cannot be unwired, so a withdrawal has to be honoured at the point of send.
					consented: () => getConsent().analytics,
				});
			} catch (error) {
				// Vitals are best-effort: a failed chunk load (offline, deploy skew) must not undo the tag
				// bootstrap above.
				console.warn(`[ui#169/ui#1638] web-vitals wiring failed: ${error}`);
			}
		})();
	}
	return telemetryWiring;
}

/**
 * Bring the Reddit pixel in line with the visitor's current choices (ui#834).
 *
 * Separate from {@link syncAnalyticsWithConsent} rather than folded into it, because the two answer
 * different questions: Google's tag serves BOTH purposes and its consent signals vary by choice, while
 * this one exists only for advertising and has no denied mode to run in. Merging them would make the
 * Reddit half read as "configured by consent" when it is really "loaded, or not at all".
 *
 * Idempotent, like its sibling. Called on mount, on every consent change and whenever the signed-in
 * identity changes, so a visitor who consents before signing in still gets their match keys attached —
 * see {@link identifyRedditVisitor} for why a second `init` could not do that.
 *
 * @param consent   the state to apply; defaults to the persisted one
 * @param matchKeys the signed-in account's advanced-matching keys, empty for a signed-out visitor
 */
export function syncRedditPixelWithConsent(
	consent: ConsentState = getConsent(), matchKeys: RedditMatchKeys = {}): void {
	if (typeof window === "undefined") {
		return; // SSG / server: never touch the pixel.
	}
	if (!consent.marketing) {
		stopRedditPixel();
		redditIdentity = null;
		// An owed signup goes with the withdrawal (api#1240). `signUpMark` states the rule for the
		// other way into this state — an event that happens under denied consent is DROPPED, never
		// replayed after a later grant — and grant, sign up, withdraw, re-grant in one tab is that same
		// replay. An invariant that holds for one entry into the denied state and not the other is not
		// one. It also matches what `stopRedditPixel` does one line up: it expires the identifiers the
		// pixel wrote, and this mark is site data held for the same purpose.
		clearSignUpPending("reddit");
		return;
	}
	const identity = JSON.stringify(matchKeys);
	if (redditPixelRunning()) {
		// Recorded only when the call actually reached the pixel. On a route outside its scope it does
		// not, and remembering the identity anyway would mean the keys were never attached once the
		// visitor reached a page where they could be.
		if (identity !== redditIdentity && identifyRedditVisitor(matchKeys)) {
			redditIdentity = identity;
		}
	} else if (startRedditPixel(redditPixelId(), consent, matchKeys)) {
		redditIdentity = identity;
	}
	// A signup this browser owes Reddit (api#1240), spent here rather than on the navigation alone,
	// because THIS is the moment the account id exists: the session is built from the Firebase identity
	// first and the `/account` payload lands after it, so a visitor who signs up and stays on the page
	// they signed up from would otherwise never report it. A navigation reaches this too, since
	// `reportRedditRouteChange` syncs before it counts the view.
	//
	// The conversion id is `matchKeys.externalId` rather than a parameter of its own, and that is the
	// point rather than a shortcut: the api derives `external_id` from the very uuid it writes as the
	// outbox row's conversion id, so the browser's two values have to be the same one. Reading both from
	// one place makes that true by construction instead of by two callers agreeing.
	//
	// Every gate that could stop it lives inside `trackRedditSignUp`: nothing is owed, the pixel is not
	// running, or this route may not carry it. Calling it unconditionally is what keeps those in one
	// place instead of restated at each caller.
	trackRedditSignUp(matchKeys.externalId);
}

/**
 * A route change, reported to the pixel (kitecorp/transferify-ui#2008).
 *
 * Two things happen on a navigation and they are one decision, which is why they are one function rather
 * than two calls a caller has to order: the pixel may now be allowed to run where it was not (the visitor
 * arrived on a page outside its scope and walked into one), and a page it was ALREADY running on has been
 * replaced by another.
 *
 * Counting the view exactly once is {@link trackRedditPageVisit}'s job, not this function's. A start
 * counts its own — `init` sends a `PageVisit` — and so does an identify that attaches match keys, and
 * reconstructing which of those happened from a snapshot taken before the sync is how one navigation
 * ends up reported twice. Both calls are no-ops on a route outside the scope, enforced inside
 * {@link ./redditPixel} rather than here.
 *
 * @param consent   the state to apply; defaults to the persisted one
 * @param matchKeys the signed-in account's advanced-matching keys, empty for a signed-out visitor
 */
export function reportRedditRouteChange(
	consent: ConsentState = getConsent(), matchKeys: RedditMatchKeys = {}): void {
	syncRedditPixelWithConsent(consent, matchKeys);
	trackRedditPageVisit();
}

/**
 * Bring the Meta pixel in line with the visitor's current choices (kitecorp/transferify#375).
 *
 * The Reddit pair's sibling, and deliberately not folded into it: the two pixels answer the consent
 * question differently. Reddit runs on any advertising grant; Meta runs only on a Meta-enabled site and
 * only for a decision given against wording that names Meta ({@link storedPurposesVersion} against
 * `metaPixel.META_SITES`), and every one of those gates lives in `./metaPixel`, not here.
 *
 * What this adds is the ORDER, which no single call can know: a withdrawal revokes and drops the owed
 * sign-up, a running pixel is told about an account that just arrived, a stopped one is started (never in
 * the page load it was revoked in, for the reason `metaPixel`'s header gives), and an owed sign-up is spent
 * the moment the account id exists — the same moment `syncRedditPixelWithConsent` spends Reddit's, for the
 * same reason.
 *
 * Idempotent. Unlike Reddit's identify, Meta's sends no event, so a re-sync with the same account sends
 * nothing and needs no identity memo here; `identifyMetaVisitor` attaches an account once per page load.
 *
 * @param consent       the state to apply; defaults to the persisted one
 * @param externalId    the signed-in account's own uuid (api#1240), or null for a signed-out visitor
 * @param storedVersion the wording version the stored decision was given against
 */
export function syncMetaPixelWithConsent(
	consent: ConsentState = getConsent(), externalId: string | null = null,
	storedVersion: number = storedPurposesVersion()): void {
	if (typeof window === "undefined") {
		return; // SSG / server: never touch the pixel.
	}
	if (!consent.marketing) {
		stopMetaPixel();
		// The owed sign-up goes with the withdrawal, for the reason the Reddit branch above states.
		clearSignUpPending("meta");
		return;
	}
	if (metaPixelRunning()) {
		identifyMetaVisitor(externalId);
	} else {
		startMetaPixel({pixelId: metaPixelId(), consent, storedVersion, externalId});
	}
	// The event id is the same uuid `init` carried as external_id, read from one place, because the
	// server's `event_id` for the account-established fact is that uuid.
	trackMetaSignUp(externalId);
}

/**
 * A route change, reported to the Meta pixel: the start it may now be allowed, then the view counted once
 * — the pairing {@link reportRedditRouteChange} states for Reddit.
 */
export function reportMetaRouteChange(consent: ConsentState = getConsent(), externalId: string | null = null): void {
	syncMetaPixelWithConsent(consent, externalId);
	trackMetaPageView();
}

/**
 * Bring analytics and advertising tagging in line with the visitor's current choices.
 *
 * @param consent the state to apply; defaults to the persisted one, which is what every caller wants
 */
export async function syncAnalyticsWithConsent(consent: ConsentState = getConsent()): Promise<void> {
	if (typeof window === "undefined") {
		return; // SSG / server: never touch analytics.
	}
	// TWO INDEPENDENT DECISIONS, and they must stay independent. The Google Ads tag rides the
	// ADVERTISING purpose and our own measurement rides the ANALYTICS one, so neither may be reached
	// through the other's gate. Writing this as one early return on the tag gate is what hid a real
	// defect: once #1929 narrowed that gate to advertising alone, an analytics-only visitor returned
	// before the telemetry branch and armed nothing at all.
	syncAdvertisingTag(consent);
	await syncOwnMeasurement(consent);
}

/**
 * The Google Ads tag, which rides the ADVERTISING purpose alone since #1929.
 *
 * Nothing about analytics appears here. A visitor who granted analytics and refused advertising must
 * cause no Google request, not even the cookieless ping Basic Consent Mode would otherwise send.
 */
function syncAdvertisingTag(consent: ConsentState): void {
	if (!googleTaggingAllowed(consent)) {
		if (tagRunning) {
			stopGoogleTagging();
			tagRunning = false;
		}
		// Independent of whether the tag ran in THIS page load. A returning browser still holds what an
		// earlier visit wrote, and the Cookie Policy promises a withdrawal deletes it; hanging the
		// clearing off `tagRunning` made that promise false for every visitor whose tag never loaded.
		clearGoogleAdvertisingCookies();
		return;
	}
	if (tagRunning) {
		updateGoogleConsent(consent);
	} else {
		tagRunning = startGoogleTagging(googleTagConfig(), consent);
	}
}

/**
 * Our own measurement — the first-party event client and the performance reporters — which rides the
 * ANALYTICS purpose alone.
 *
 * It works whether or not Google's tag loaded, and a large share of visitors block that tag, so tying
 * our own performance measurement to its availability would silently bias the field sample toward
 * people without ad blockers (ui#1638).
 */
async function syncOwnMeasurement(consent: ConsentState): Promise<void> {
	if (consent.analytics) {
		await wireTelemetry();
		return;
	}
	// The identifier goes with the permission, on EVERY path out of the analytics purpose — including
	// the one that leaves the advertising tag running. The page-view mark goes with it, so a visitor
	// who withdraws and grants again on the same page is reported rather than dropped as a repeat of
	// the visit they just revoked.
	clearAnalyticsClientId();
	forgetLastReportedPageView();
	// The GA4 cookies an EARLIER build wrote. This build sets none, so there is usually nothing to
	// remove — but the published policy tells a returning reader that withdrawing deletes them, and
	// since #1929 the tag they used to hang off never loads for an analytics-only visitor.
	clearGoogleAnalyticsCookies();
	// Gated on TELEMETRY having been armed, not on any tag having started (ui#1638): telemetry can be
	// running while the tag never did, and the reporter would otherwise stay installed after a
	// withdrawal in exactly those cases. Still conditional, so an undecided visitor who never armed
	// anything tears nothing down.
	if (telemetryWiring !== null) {
		setPanelLatencyReporter(null);
	}
}
